import { combineReducers, configureStore, createSelector } from '@reduxjs/toolkit'
import { EventTypes } from 'types/events'
import appSlice from './slices/appSlice'
import configSlice from './slices/configSlice'
import mapSlice from './map/mapSlice'
import locationSlice from './location/locationSlice'
import tempMarkerSlice from './tempMarker/tempMarkerSlice'
import currentEventSlice from './currentEvent/currentEventSlice'
import callHistorySlice from './callHistory/callHistorySlice'
import recentDispatchSlice from './recentDispatch/recentDispatchSlice'
import { FormApi } from 'final-form'
import { FormName, Forms } from 'types/form'
import { LocationState } from 'types/location'
import { TransformedTicket } from 'types/ticket'
import kongSlice from './slices/kongSlice'

const reducer = combineReducers({
  appSlice,
  configSlice,
  callHistorySlice,
  currentEventSlice,
  locationSlice,
  mapSlice,
  tempMarkerSlice,
  kongSlice,
  recentDispatchSlice,
})

export type ReduxState = ReturnType<typeof reducer>

export const loadState = () => {
  try {
    const serializedState = localStorage.getItem('state')
    if (serializedState === null) {
      return undefined
    }
    return JSON.parse(serializedState)
  } catch (e) {
    return undefined
  }
}

const saveState = (state) => {
  try {
    const serializedState = JSON.stringify(state)
    localStorage.setItem('state', serializedState)
  } catch (e) {
    // Ignore write errors;
  }
}

const peristedState = loadState()

/**
 * Creates a new store. Avoiding singletons here so tests are cleaner.
 * @returns A new instance of a HERO store.
 */
export const getStore = () => {
  const store = configureStore({
    reducer,
    preloadedState: peristedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
  })
  store.subscribe(() => {
    saveState(store.getState())
  })

  return store
}

// AppSlice
export const selectSidebarState = (state: ReduxState) => (side: 'left' | 'right') => state.appSlice.sidebar[side]
export const selectFormsState = (state: ReduxState) => state.appSlice.forms
export const selectTabsState = (state: ReduxState) => state.appSlice.tabs
export const selectLocalQuickNotes = (state: ReduxState) => state.appSlice.quickNotes
export const selectCustomVinInput = (state: ReduxState) => state.appSlice.customVin.vinInput
export const selectCallType = (state: ReduxState) => state.appSlice.forms.callType

// ConfigSlice
export const selectCurrentUser = (state: ReduxState) => state.configSlice.auth.user

// TODO: Update FORM selector to take in specific form instead of calling for all forms
export const selectSingleForm = (formName: FormName) =>
  createSelector(selectFormsState, (formState: Forms) => {
    const form = formState[formName] as FormApi
    return !form || (form && !form.getState) ? null : form.getState().values
  })

export const selectMapViewport = (state: ReduxState) => state.mapSlice.viewport
export const selectMapType = (state: ReduxState) => state.mapSlice.mapType

export const selectMapShouldFitBounds = (state: ReduxState) => state.mapSlice.viewport.shouldFitBounds

export const selectMapCenter = (state: ReduxState) => state.mapSlice.viewport.center
export const selectMapZoom = (state: ReduxState) => state.mapSlice.viewport.zoom

export const selectLocation = (state: ReduxState) => (name: keyof LocationState) => state.locationSlice[name]
export const selectTempMarker = (state: ReduxState) => state.tempMarkerSlice.tempMarker
export const selectSinglePinView = (state: ReduxState) => state.tempMarkerSlice.singlePinView
export const selectAllEvents = (state: ReduxState) => state.currentEventSlice
export const selectCurrentEvent = (state: ReduxState) => (eventType?: EventTypes) => {
  if (eventType && state.currentEventSlice[eventType]) {
    return state.currentEventSlice[eventType]
  }

  const filteredEvents = Object.entries(state.currentEventSlice).filter(([key, value]) => Boolean(value))[0]
  if (eventType && filteredEvents && filteredEvents[0] === eventType) {
    // Event object value from key
    return filteredEvents && filteredEvents[1]
  }
  return null
}
export const selectCurrentSoftServiceEvent = (state: ReduxState) =>
  state.currentEventSlice[EventTypes.FUEL] ??
  state.currentEventSlice[EventTypes.TIRES] ??
  state.currentEventSlice[EventTypes.TIRES_CHANGE_SPARE] ??
  state.currentEventSlice[EventTypes.WINCH] ??
  state.currentEventSlice[EventTypes.LOCKOUT] ??
  state.currentEventSlice[EventTypes.JUMPSTART]

export const selectTicketHistoryEvents = (state: ReduxState) => state.callHistorySlice.events

// Kongslice
export const selectTicket = (state: ReduxState) => state.kongSlice.multiRetrieve.ticket as TransformedTicket
export const selectTicketError = (state: ReduxState) => state.kongSlice.multiRetrieve.ticketError
export const selectSmartAssignment = (state: ReduxState) => state.kongSlice.entegral
export const selectFleetVehicle = (state: ReduxState) => state.kongSlice.fleetVehicle.vehicle
export const selectFleetVehicleHistory = (state: ReduxState) => state.kongSlice.fleetVehicle.vehicleHistory
export const selectLastVinSearched = (state: ReduxState) => state.kongSlice.connectedCar.lastVinSearched
export const selectRecentDispatches = (state: ReduxState) => state.recentDispatchSlice

export const selectFleetVehicleFromVin = (vin: string) =>
  createSelector([selectFleetVehicle, selectFleetVehicleHistory], (fleetVehicle, fleetVehicleHistory) => {
    if (!fleetVehicle || !fleetVehicleHistory) {
      return null
    }

    // Check if the current vehicle matches the VIN
    if (fleetVehicle.vehicleAsset?.vin === vin) {
      return fleetVehicle
    }

    // Search through the vehicle history for a match
    const matchingVehicle = fleetVehicleHistory.find((vehicle) => vehicle.vehicleAsset?.vin === vin)

    return matchingVehicle || null
  })

export const selectVehicleFuelType = createSelector(selectFleetVehicle, (fleetVehicle) => {
  const fuelCategorySplit = fleetVehicle?.vehicleAsset?.specification?.fuelCategory?.split('fuelCategory:')
  const fuelCategoryString = fuelCategorySplit && fuelCategorySplit[1]
  const fuelType = fleetVehicle?.vehicleAsset?.specification?.fuelType

  const isElectric = fuelCategoryString === 'ELECTRIC' || fuelType === 'Electric'

  return isElectric ? 'ELECTRIC' : 'GAS'
})
